// What the builder reads of a Quote Brain run's questions and findings
// (Peter, 2026-10-28: "This stuff shouldn't show to the customer").
//
// The run writes its questions and findings for itself: it names the job's
// files by their run ids (E1), past quotes by theirs (Q8), validator rules by
// theirs (V23), items by theirs (I3), and the rulebook's reference files
// ("pricing-bands.md"). This puts those into the builder's words, and only
// those (review V2): an id is replaced only when it is one of THIS run's ids,
// where it stands, and no text around it is ever removed. So "E2.2" (an NCC
// clause), "HM-V2", "Q4 2026" and "(Q8 $22,059, Q9 $33,300)" keep every word
// and figure the builder needs:
//   - a file id reads as the file's name, in quotes;
//   - a past quote id reads "a past quote" (not before a year: "Q4 2026");
//   - a rule code reads "a check";
//   - an item id reads as the item number when the caller knows it;
//   - a rulebook reference file reads "the house rules".
// Pure and shared: the browser imports this file directly.

// An id token on its own: not part of a longer word, code or clause number.
const ID_TOKEN_RE = /(?<![\w.\-/])([EQVI])(\d{1,3})(?![\w\-/]|\.\d)/g;
const YEAR_AFTER_RE = /^\s+(?:19|20)\d{2}\b/;
const REFERENCE_FILE_RE = /\b(?:[\w-]+\/)*[\w-]+\.md\b/gi;

const hasId = (set, id) => !!set && typeof set.has === 'function' && set.has(id);

/**
 * builderText(text, { files, quotes, rules, itemLabel }) -> the text as the builder reads it.
 * @param {unknown} text
 * @param {{
 *   files?: Map<string, string> | null,
 *   quotes?: Set<string> | null,
 *   rules?: Set<string> | null,
 *   itemLabel?: ((id: string) => string) | null,
 * }} [ids] this run's ids: files by id with their names, past quote ids, rule codes; itemLabel(id) -> "Item 2" or ''
 * @returns {string}
 */
export function builderText(text, { files = null, quotes = null, rules = null, itemLabel = null } = {}) {
    const source = String(text ?? '');
    return source
        .replace(ID_TOKEN_RE, (token, kind, _n, offset) => {
            if (kind === 'E') {
                if (!hasId(files, token)) return token;
                const name = String(files.get(token) || '').trim();
                return name ? `"${name}"` : 'a job file';
            }
            if (kind === 'Q') {
                if (!hasId(quotes, token) || YEAR_AFTER_RE.test(source.slice(offset + token.length))) return token;
                return 'a past quote';
            }
            if (kind === 'V') return hasId(rules, token) ? 'a check' : token;
            const label = typeof itemLabel === 'function' ? itemLabel(token) : '';
            return label || token;
        })
        .replace(REFERENCE_FILE_RE, 'the house rules')
        .trim();
}

/**
 * The ids a run's text may name (review V2): its files with their names (the
 * evidence snapshot's manifest), the past quotes it was shown or read (the
 * candidate's lanes used), and the rule codes its own findings carry.
 */
export function runTextIds({ evidence = null, lanesUsed = null, candidate = null } = {}) {
    const files = new Map();
    for (const f of evidence?.manifest || []) if (f?.fileId) files.set(String(f.fileId), String(f.label || ''));
    const quotes = new Set();
    for (const l of Array.isArray(lanesUsed) ? lanesUsed : []) if (l?.quoteId) quotes.add(String(l.quoteId));
    const rules = new Set();
    for (const f of [...(candidate?.findings || []), ...(candidate?.unresolved || []), ...(candidate?.priced?.flags || [])]) {
        const m = /^(V\d{1,2})(?:-|$)/.exec(String(f?.code || ''));
        if (m) rules.add(m[1]);
    }
    return { files, quotes, rules };
}
